/* ==========================================================================
   TODAY Fitness Equipment — colourway preview

   The renders are supplied in one finish: a greyscale frame with saturated
   warm detailing on the pads and guards. Measured on T901, that detailing is
   ~7.6% of the subject and sits almost entirely in the 10–45° hue band, with
   everything else desaturated. That separation is what makes an honest
   recolour possible: we remap only strongly-saturated warm pixels and leave
   the frame, the floor and the shadows alone.

   Shading is preserved by keeping each pixel's own lightness and only
   replacing hue and saturation, so highlights and creases in the upholstery
   survive the swap.

   This is a preview, not a colour-matched proof. The page says so.
   ========================================================================== */
(function () {
  'use strict';

  /* Pixels we treat as "the detailing". Deliberately conservative — better to
     leave a little orange than to bleed colour into the frame. */
  var HUE_MIN = 5;
  var HUE_MAX = 50;
  var SAT_MIN = 0.30;
  var VAL_MIN = 0.12;

  function rgbToHsl(r, g, b) {
    r /= 255; g /= 255; b /= 255;
    var mx = Math.max(r, g, b), mn = Math.min(r, g, b);
    var h = 0, s = 0, l = (mx + mn) / 2, d = mx - mn;
    if (d > 0.0001) {
      s = l > 0.5 ? d / (2 - mx - mn) : d / (mx + mn);
      if (mx === r) h = ((g - b) / d) % 6;
      else if (mx === g) h = (b - r) / d + 2;
      else h = (r - g) / d + 4;
      h *= 60;
      if (h < 0) h += 360;
    }
    return [h, s, l, mx];
  }

  function hueToRgb(p, q, t) {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  }

  function hslToRgb(h, s, l) {
    h = ((h % 360) + 360) % 360 / 360;
    if (s === 0) { var v = Math.round(l * 255); return [v, v, v]; }
    var q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    var p = 2 * l - q;
    return [
      Math.round(hueToRgb(p, q, h + 1 / 3) * 255),
      Math.round(hueToRgb(p, q, h) * 255),
      Math.round(hueToRgb(p, q, h - 1 / 3) * 255)
    ];
  }

  function hexToHsl(hex) {
    hex = hex.replace('#', '');
    if (hex.length === 3) hex = hex.split('').map(function (c) { return c + c; }).join('');
    var r = parseInt(hex.slice(0, 2), 16),
        g = parseInt(hex.slice(2, 4), 16),
        b = parseInt(hex.slice(4, 6), 16);
    return rgbToHsl(r, g, b);
  }

  /* Recolour in place. `target` null means "as supplied" — leave it alone. */
  function recolour(imageData, targetHex) {
    var d = imageData.data;
    var t = hexToHsl(targetHex);
    var th = t[0], ts = t[1], tl = t[2];
    /* A near-neutral target (black, graphite) can't take a hue, so we
       desaturate and darken instead of rotating. */
    var neutral = ts < 0.12;

    for (var i = 0; i < d.length; i += 4) {
      if (d[i + 3] < 8) continue;
      var hsl = rgbToHsl(d[i], d[i + 1], d[i + 2]);
      var h = hsl[0], s = hsl[1], l = hsl[2], v = hsl[3];
      if (s < SAT_MIN || v < VAL_MIN) continue;
      if (h < HUE_MIN || h > HUE_MAX) continue;

      /* Feather the edge of the band so the recolour doesn't cut a hard line
         through anti-aliased pixels. */
      var edge = Math.min((h - HUE_MIN) / 8, (HUE_MAX - h) / 8, (s - SAT_MIN) / 0.12, 1);
      if (edge <= 0) continue;
      edge = Math.min(edge, 1);

      var out;
      if (neutral) {
        out = hslToRgb(0, 0, l * (0.35 + tl * 0.5));
      } else {
        /* keep this pixel's own lightness, nudged toward the target's, so a
           dark target reads dark without flattening the shading */
        var nl = l * 0.72 + tl * 0.28;
        out = hslToRgb(th, Math.min(ts * (0.7 + s * 0.5), 1), nl);
      }
      d[i]     = Math.round(d[i]     + (out[0] - d[i])     * edge);
      d[i + 1] = Math.round(d[i + 1] + (out[1] - d[i + 1]) * edge);
      d[i + 2] = Math.round(d[i + 2] + (out[2] - d[i + 2]) * edge);
    }
    return imageData;
  }

  function initOne(root) {
    if (root.dataset.tfColourway === 'on') return;
    root.dataset.tfColourway = 'on';

    var img = root.querySelector('.tf-cw-img');
    var canvas = root.querySelector('[data-tf-cw-canvas]');
    var swatches = Array.prototype.slice.call(root.querySelectorAll('[data-tf-cw-swatch]'));
    var nameEl = root.querySelector('[data-tf-cw-name]');
    if (!img || !canvas || !swatches.length) return;

    var ctx = canvas.getContext('2d', { willReadFrequently: true });
    var base = null;   /* pristine pixels, re-read for every swap */

    function capture() {
      var w = img.naturalWidth, h = img.naturalHeight;
      if (!w || !h) return false;
      /* cap the working size — a 3840px render is far more than the page
         shows and would cost ~40x the pixels to process */
      var max = 1400;
      var scale = Math.min(1, max / w);
      canvas.width = Math.round(w * scale);
      canvas.height = Math.round(h * scale);
      try {
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        base = ctx.getImageData(0, 0, canvas.width, canvas.height);
        return true;
      } catch (e) {
        /* tainted canvas — the image is cross-origin without CORS headers */
        return false;
      }
    }

    function apply(hex, label) {
      if (!base) { if (!capture()) { fail(); return; } }
      if (!hex) {
        ctx.putImageData(base, 0, 0);
      } else {
        var copy = new ImageData(
          new Uint8ClampedArray(base.data),
          base.width,
          base.height
        );
        ctx.putImageData(recolour(copy, hex), 0, 0);
      }
      canvas.hidden = false;
      img.style.visibility = 'hidden';
      if (nameEl && label) nameEl.textContent = label;
    }

    function fail() {
      /* Leave the original image showing and retire the control rather than
         presenting a swatch row that does nothing. */
      root.classList.add('is-unavailable');
      canvas.hidden = true;
      img.style.visibility = '';
    }

    function select(btn) {
      swatches.forEach(function (s) {
        s.setAttribute('aria-checked', String(s === btn));
      });
      apply(btn.dataset.tfCwSwatch || null, btn.dataset.tfCwLabel || '');
    }

    swatches.forEach(function (btn) {
      btn.addEventListener('click', function () { select(btn); });
      btn.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
          e.preventDefault();
          var n = swatches[(swatches.indexOf(btn) + 1) % swatches.length];
          n.focus(); select(n);
        }
        if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
          e.preventDefault();
          var p = swatches[(swatches.indexOf(btn) - 1 + swatches.length) % swatches.length];
          p.focus(); select(p);
        }
      });
    });

    function ready() {
      if (!capture()) { fail(); return; }
      var current = swatches.filter(function (s) {
        return s.getAttribute('aria-checked') === 'true';
      })[0] || swatches[0];
      /* the default swatch is "as supplied", so nothing is drawn over it */
      if (current.dataset.tfCwSwatch) select(current);
    }

    if (img.complete && img.naturalWidth) ready();
    else img.addEventListener('load', ready, { once: true });
    img.addEventListener('error', fail, { once: true });
  }

  function initAll(root) {
    (root || document).querySelectorAll('[data-tf-colourway]').forEach(initOne);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
})();
